import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionableNotification,
  RadioTile,
  Search,
  SkeletonText,
  TileGroup,
} from '@carbon/react';
import moment from 'moment';
import { US_DATE_FORMAT } from 'utils/dates';
import { DrillInHeader } from './drill-in-header';
import { useDomainScenarios } from './hooks';
import { CASE_DOMAIN_LABELS } from './utils';
import type { ScenarioPickerContext } from './types';
import styles from './scenario-picker-drill-in.module.scss';

/** Above this many options the client-side search field appears (mock parity). */
const SEARCH_THRESHOLD = 6;

export interface ScenarioPickerDrillInProps {
  /** Which (area × domain) is being edited, + the current effective scenario id. */
  context: ScenarioPickerContext;
  /** Commit a scenario for this (area × domain) — the section stores the override + returns to main. */
  onPick: (scenarioId: number) => void;
  /** Return to the main panel view. */
  onBack: () => void;
}

/**
 * The Scenario Picker drill-in (P4 FR#8): the per-run scenario choices for one
 * (area × domain). Options come from `useDomainScenarios(context.domain)` — a
 * cache hit from the areas list's prefetch (T13). A search field shows only when
 * there are more than six options (client-side name filter); the tiles are
 * RadioTiles (name + "Updated [date]") with the current effective scenario
 * (`context.currentScenarioId`) preselected and scrolled into view, so the
 * active choice is visible on open however long the list is. Picking a tile
 * commits the override (`onPick`) and KEEPS the picker open, with the check
 * moved to the clicked tile — the user leaves via the header's back arrow, so a
 * pick is visibly confirmed instead of the surface vanishing under the click.
 * The header and search stay pinned while only the tiles scroll. Loading → tile
 * skeleton; error → notification + Retry; a search with no match → "No
 * scenarios match …".
 *
 * @param {ScenarioPickerDrillInProps} props - The (area × domain) context + the pick/back handlers.
 * @return {JSX.Element} The scenario picker drill-in surface.
 */
export function ScenarioPickerDrillIn({
  context,
  onPick,
  onBack,
}: ScenarioPickerDrillInProps) {
  const { data, isPending, isError, refetch } = useDomainScenarios(
    context.domain
  );

  const [query, setQuery] = useState('');
  // The checked tile, held locally so a pick shows immediately: `context` is a
  // snapshot taken when the picker opened and does not change under us.
  const [selectedId, setSelectedId] = useState(context.currentScenarioId);
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // One auto-scroll per opened picker — later re-renders (a pick, a search)
  // must not yank the list back to the checked tile.
  const hasScrolledRef = useRef(false);
  const options = useMemo(() => data ?? [], [data]);
  const showSearch = options.length > SEARCH_THRESHOLD;
  const term = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!term) {
      return options;
    }
    return options.filter((option) => option.name.toLowerCase().includes(term));
  }, [options, term]);

  // Bring the checked tile into view once the list has rendered. Without this a
  // long list opens at the top and the user cannot see which scenario is active.
  // `block: 'nearest'` leaves an already-visible tile alone; the sticky header
  // is measured rather than assumed, because its height varies with the search
  // field and the wrapped eyebrow line.
  useEffect(() => {
    if (hasScrolledRef.current || selectedId === null || options.length === 0) {
      return;
    }
    const root = rootRef.current;
    const input = root?.querySelector<HTMLElement>(
      `#eval3-scenario-tile-${selectedId}`
    );
    if (!input) {
      return;
    }
    hasScrolledRef.current = true;
    // The input's wrapper div: Carbon renders the label as the input's
    // SIBLING inside that div, so there is no label ancestor to climb to.
    const tile = input.parentElement ?? input;
    tile.style.scrollMarginTop = `${headerRef.current?.offsetHeight ?? 0}px`;
    tile.scrollIntoView({ block: 'nearest' });
  }, [options, selectedId]);

  return (
    <div className={styles.picker} ref={rootRef}>
      {/* Pinned to the top of the panel's scrollport: the back arrow (the only
          way out of the drill-in) and the search must stay reachable however
          far the tile list is scrolled. */}
      <div className={styles.stickyHeader} ref={headerRef}>
        <DrillInHeader
          eyebrow={`${context.areaName} · Select scenario`}
          title={CASE_DOMAIN_LABELS[context.domain]}
          onBack={onBack}
        />

        {showSearch && (
          <Search
            id={`eval3-scenario-search-${context.domain}`}
            size="sm"
            labelText="Search scenarios"
            placeholder="Search scenarios"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
      </div>

      {isPending && <SkeletonText paragraph lineCount={5} />}

      {isError && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the scenarios"
          subtitle="The scenario list is unavailable right now."
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {!isPending && !isError && filtered.length === 0 && (
        <p className={styles.emptyText}>
          {term
            ? `No scenarios match "${query.trim()}".`
            : 'No scenarios available for this domain.'}
        </p>
      )}

      {filtered.length > 0 && (
        <TileGroup
          className={styles.tileGroup}
          name={`eval3-scenario-picker-${context.domain}`}
          legend="Available scenarios"
          // Preselect the current effective scenario (override ?? saved), then
          // follow the user's picks. Stringified — TileGroup compares tile
          // values strictly.
          valueSelected={selectedId !== null ? String(selectedId) : undefined}
          // Re-selecting the already-checked tile never reaches here: TileGroup
          // filters it (`if (value !== selected)`, TileGroup.js:37), on the
          // keyboard path as well as the mouse one. RadioTile on its own has no
          // such guard, so that filtering is a property of the COMPOSITION —
          // pinned by the picker's keyboard tests, because swapping TileGroup
          // out would make a no-op keystroke dirty the form.
          onChange={(value: string | number) => {
            setSelectedId(Number(value));
            onPick(Number(value));
          }}
        >
          {filtered.map((option) => (
            <RadioTile
              key={option.id}
              id={`eval3-scenario-tile-${option.id}`}
              value={String(option.id)}
            >
              {/* Name over "Updated [date]" (mock §10b — name + updated only;
                  the scenarioSummary lives on the area rows, not here). Carbon
                  nests a RadioTile's children in its own content wrapper, so the
                  vertical stack lives on this inner flex column (a class on the
                  tile itself wouldn't reach the spans). */}
              <div className={styles.tileBody}>
                <span className={styles.tileName}>{option.name}</span>
                <span className={styles.tileMeta}>
                  Updated {moment(option.updatedAt).format(US_DATE_FORMAT)}
                </span>
              </div>
            </RadioTile>
          ))}
        </TileGroup>
      )}
    </div>
  );
}
